import type { ShipmentManifestsReport } from "#/types/manifest";
import { useTranslation } from "react-i18next";
import ReportSection from "./ReportSection";

interface ShipmentManifestsSectionProps {
  data: ShipmentManifestsReport;
}

interface CountCardProps {
  label: string;
  count: number;
  /** Static Tailwind classes so the colours survive the build. */
  classes: { card: string; dot: string; count: string };
}

const CountCard = ({ label, count, classes }: CountCardProps) => (
  <div className={`flex flex-col gap-1 rounded-lg border border-l-4 p-3 ${classes.card}`}>
    <div className="flex items-center gap-1.5">
      <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${classes.dot}`} />
      <span className="truncate text-[11px] font-semibold uppercase tracking-wider text-gray-500">
        {label}
      </span>
    </div>
    <span className={`text-2xl font-extrabold ${classes.count}`}>{count.toLocaleString()}</span>
  </div>
);

/** # HAWBs, # completed at exit, # with an exit issue. */
const ShipmentManifestsSection = ({ data }: ShipmentManifestsSectionProps) => {
  const { t } = useTranslation();
  return (
    <ReportSection title={t("reports.shipmentManifests.title")}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <CountCard
          label={t("reports.shipmentManifests.total")}
          count={data.total}
          classes={{
            card: "border-indigo-200 border-l-indigo-500 bg-indigo-50",
            dot: "bg-indigo-500",
            count: "text-indigo-800",
          }}
        />
        <CountCard
          label={t("reports.shipmentManifests.completed")}
          count={data.completed}
          classes={{
            card: "border-green-200 border-l-green-500 bg-green-50",
            dot: "bg-green-500",
            count: "text-green-800",
          }}
        />
        <CountCard
          label={t("reports.shipmentManifests.issues")}
          count={data.issues}
          classes={{
            card: "border-red-200 border-l-red-500 bg-red-50",
            dot: "bg-red-500",
            count: "text-red-800",
          }}
        />
      </div>
    </ReportSection>
  );
};

export default ShipmentManifestsSection;
